/* Tailwind also imports theme/css/Liveauctioneers.css */
import '@/tailwind.css';
import '@/theme/theme.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import ErrorBoundary from '@liveauctioneers/caterwaul-components/lib/ErrorBoundary/ErrorBoundary';
import { initCookiebotBannerDelegation } from '@liveauctioneers/hammer-ui-core/cookieConsent';
import { HammerUIProvider } from '@liveauctioneers/hammer-ui-core/providers';
import { loadableReady } from '@loadable/component';
import * as React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import ReactModal from 'react-modal';
import { BrowserRouter } from 'react-router-dom';
import { PersistorConfig, persistStore } from 'redux-persist';

import { getBasename } from '@/client/getBasename';
import { initializeAmplitudeAnalyticsAndExperimentation } from '@/components/AmplitudeExperimentsProvider/initializeAmplitudeSDK.client';
import Provider from '@/components/IntlRedux/Provider';
import { PreviousLocationProvider } from '@/components/PreviousLocation/PreviousLocation';
import { setDeviceId } from '@/network/deviceIdContext.client';
import App from '@/pages/App/App';
import { logClientPerformanceStats } from '@/redux/modules/apiPerformanceStats';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getInitialClientStore } from '@/redux/store';
import AppRoutes, { routes as getRoutes } from '@/routes';
import { isItemPage404 } from '@/ssr/utils/isItemPage404';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import * as apiPerfLogger from '@/utils/apiPerformanceLogger';
import { buildUrlFromLocation } from '@/utils/urls';

import { AmplitudeExperimentsClientProvider } from './AmplitudeExperimentsClientProvider';
import NoCookies from './NoCookies';
import RouteDataLoader from './RouteDataLoader';

const localStoragePersistConfig: PersistorConfig = {
    debounce: 33,
    keyPrefix: 'la:',
    whitelist: ['forgotPassword', 'persist', 'recentItem', 'walkthroughs'],
};

const helmetContext = {};

const localStorageCheck = () => {
    const test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch {
        return false;
    }
};

// Create the redux store and routing history
const store = getInitialClientStore();

initCookiebotBannerDelegation();

const amplitudeDeploymentKey = store.getState().config.amplitudeDeploymentKey;

initializeAmplitudeAnalyticsAndExperimentation(amplitudeDeploymentKey, {
    hasEverPlacedBid: store.getState().user.userData.hasEverPlacedBid,
    hasEverRegisteredForAuction: store.getState().user.userData.hasEverRegisteredForAuction,
}).then(({ amplitudeExperimentsClient }) => {
    /**
     * Wait for all loadable components to be loaded
     * @see https://loadable-components.com/docs/api-loadable-component/#loadableready
     */
    loadableReady(() => {
        /**
         * Render the client-side app
         */

        const dest = document.getElementById('content');
        const basename = getBasename();

        ReactModal.setAppElement('#content');

        const isWhiteLabel = getIsWhiteLabel(store.getState());

        setDeviceId(window.__amplitudeExperiments.deviceId);

        // Get application routes
        const routes = getRoutes({
            amplitudeExperimentFlags: window.__amplitudeExperiments.variants,
            featureFlags: window.__featureFlags,
            isWhiteLabel,
        });

        // sanitize and redirect pathname if it contains %0d or %0a (crlf injection)
        if (
            window?.location?.pathname.toLowerCase().includes('%0d') ||
            window?.location?.pathname.toLowerCase().includes('%0a')
        ) {
            const pathname = window.location.pathname.replace(/(.*?)%0[dD]|%0[aA].*/gm, '$1');
            const location = {
                hash: window.location?.hash,
                pathname: pathname,
                search: window.location?.search,
            };
            const redirect = buildUrlFromLocation(location, window.location?.origin);
            window.location.assign(redirect);
            return;
        }

        // record api performance metrics
        const apiMetrics = apiPerfLogger.logger(performance);

        // save to local storage
        persistStore(store, localStoragePersistConfig);

        if (!localStorageCheck() && dest) {
            const root = createRoot(dest!);
            return root.render(<NoCookies />);
        }

        // handle item-type pages that do not redirect to not-found,
        // but instead leave URL the same and return status 404
        const is404ItemPage = isItemPage404(window?.location?.pathname, store.getState());

        // compute performance stats from recorded metrics
        setTimeout(() => {
            const computedApiStats = apiPerfLogger.getStats(apiMetrics);
            store.dispatch(logClientPerformanceStats(computedApiStats, window.location.href));
        }, 5000);

        const TheApp = () => (
            <HammerUIProvider
                featureFlags={window.__featureFlags}
                theme={mergedCaterwaulAndHammerUIThemes}
            >
                <ErrorBoundary>
                    <HelmetProvider context={helmetContext}>
                        <Provider
                            key="provider"
                            store={store}
                        >
                            <AmplitudeExperimentsClientProvider
                                client={amplitudeExperimentsClient}
                                deviceId={window.__amplitudeExperiments.deviceId}
                                userId={window.__amplitudeExperiments.userId}
                                variants={window.__amplitudeExperiments.variants}
                            >
                                <BrowserRouter basename={basename}>
                                    <PreviousLocationProvider>
                                        <App>
                                            <RouteDataLoader
                                                routes={routes}
                                                store={store}
                                            >
                                                <AppRoutes
                                                    is404ItemPage={is404ItemPage}
                                                    whiteLabel={isWhiteLabel}
                                                />
                                            </RouteDataLoader>
                                        </App>
                                    </PreviousLocationProvider>
                                </BrowserRouter>
                            </AmplitudeExperimentsClientProvider>
                        </Provider>
                    </HelmetProvider>
                </ErrorBoundary>
            </HammerUIProvider>
        );

        hydrateRoot(dest, <TheApp />);

        if (process.env.NODE_ENV !== 'production') {
            window.React = React; // enable debugger
        }
    });
});
